// Worker/Better Auth client wrapper. Keeps the historical `SupaWrapper` surface
// (createSupa, signIn/signUp/..., invokeEdge, client.from) so auth.ts, remote.ts,
// audit.ts and the 25 view modules compile + run unchanged — but talks to the
// Cloudflare Worker (Hono + Better Auth + D1) instead of Supabase.

import { createAuthClient } from 'better-auth/client';
import { createPgFrom } from './db/postgrest-shim';
import type { Role, UserProfile } from './db/types';

export interface RuntimeConfig {
  STORAGE_MODE?: string;
}

const ERROR_CODES: Record<string, string> = {
  '401': 'Unauthorized — sesi tidak valid. Login ulang.',
  '403': 'Forbidden — peran tidak mencukupi (guardrail menolak).',
  '409': 'Konflik — mis. tidak boleh menghapus super_admin terakhir.',
  forbidden: 'Operasi ditolak oleh guardrail (peran/kepemilikan).',
  unauthorized: 'Sesi tidak valid. Login ulang.',
};

export function explainError(e: unknown): string {
  if (!e || typeof e !== 'object') return String(e);
  const err = e as { code?: string | number; message?: string; status?: number };
  const code = String(err.code ?? err.status ?? '');
  const msg = err.message ?? JSON.stringify(err);
  return `[${code || 'err'}] ${ERROR_CODES[code] ?? msg}`;
}

export type ProfileFetchResult =
  | { kind: 'ok'; profile: UserProfile }
  | { kind: 'profile_missing' }
  | { kind: 'invalid_role'; raw: string }
  | { kind: 'error'; message: string };

export interface SupaWrapper {
  client: any;
  configured: boolean;
  configIssue: string | null;
  url: string;
  anonPreview: string;
  storageMode: 'local' | 'supabase';
  explainError: typeof explainError;
  signIn(email: string, password: string): Promise<void>;
  signInGoogle(): Promise<void>;
  signUp(email: string, password: string, fullName?: string): Promise<{ needsConfirm: boolean }>;
  resetPassword(email: string): Promise<void>;
  updatePassword(newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  getSession(): Promise<{ user: { id: string; email?: string } | null }>;
  fetchUserProfile(): Promise<UserProfile | null>;
  fetchUserProfileDetailed(): Promise<ProfileFetchResult>;
  invokeEdge<T>(name: string, body: unknown): Promise<T>;
  setConfig(url: string, anon: string): void;
  setMode(mode: 'local' | 'supabase'): void;
}

const VALID_ROLES: Role[] = ['super_admin', 'project_admin', 'source_person', 'viewer'];
function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (VALID_ROLES as string[]).includes(v);
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<{ status: number; body: T }> {
  const res = await fetch(path, { credentials: 'include', ...init });
  const body = (await res.json().catch(() => null)) as T;
  return { status: res.status, body };
}

export function createSupa(_opts?: { storageModePreference?: 'auto' | 'supabase' | 'local' }): SupaWrapper {
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost:8787';
  const authClient = createAuthClient({ baseURL: origin });
  const from = createPgFrom();
  const listeners = new Set<(evt: string) => void>();
  const emit = (e: string) => {
    // Session lives in an httpOnly cookie (not readable sync); mirror a flag
    // so AuthApi.isAuthed() (sync) stays correct.
    try {
      if (e === 'SIGNED_IN') localStorage.setItem('ifi_authed', '1');
      if (e === 'SIGNED_OUT') localStorage.removeItem('ifi_authed');
    } catch {
      /* storage unavailable */
    }
    listeners.forEach((cb) => cb(e));
  };

  const client = {
    from,
    auth: {
      onAuthStateChange(cb: (evt: string) => void) {
        listeners.add(cb);
        return { data: { subscription: { unsubscribe: () => listeners.delete(cb) } } };
      },
      async getUser() {
        const s: any = await authClient.getSession();
        return { data: { user: s?.data?.user ?? null } };
      },
      async getSession() {
        const s: any = await authClient.getSession();
        return { data: { session: s?.data?.user ? { user: s.data.user } : null } };
      },
    },
  };

  return {
    client,
    configured: true,
    configIssue: null,
    url: origin,
    anonPreview: '',
    storageMode: 'supabase',
    explainError,

    async signIn(email, password) {
      const r: any = await authClient.signIn.email({ email: email.trim().toLowerCase(), password });
      if (r?.error) throw new Error(r.error.message || 'Login gagal.');
      emit('SIGNED_IN');
    },

    async signInGoogle() {
      // Redirect flow: browser -> Google -> /api/auth/callback/google -> callbackURL.
      await authClient.signIn.social({ provider: 'google', callbackURL: '/admin.html' });
    },

    async signUp(email, password, fullName) {
      const r: any = await authClient.signUp.email({
        email: email.trim().toLowerCase(),
        password,
        name: fullName || email.split('@')[0] || email,
      });
      if (r?.error) throw new Error(r.error.message || 'Pendaftaran gagal.');
      emit('SIGNED_IN');
      return { needsConfirm: false };
    },

    async resetPassword(email) {
      const fn = (authClient as any).forgetPassword ?? (authClient as any).requestPasswordReset;
      if (fn) await fn({ email: email.trim().toLowerCase(), redirectTo: origin + '/admin.html#reset' });
    },

    async updatePassword(newPassword) {
      const fn = (authClient as any).changePassword;
      if (!fn) throw new Error('Ubah password butuh tautan email (belum aktif di v1).');
      const r = await fn({ newPassword });
      if (r?.error) throw new Error(r.error.message);
    },

    async signOut() {
      await authClient.signOut();
      emit('SIGNED_OUT');
    },

    async getSession() {
      const s: any = await authClient.getSession();
      const u = s?.data?.user;
      return { user: u ? { id: u.id, email: u.email ?? undefined } : null };
    },

    async fetchUserProfile() {
      const r = await this.fetchUserProfileDetailed();
      return r.kind === 'ok' ? r.profile : null;
    },

    async fetchUserProfileDetailed(): Promise<ProfileFetchResult> {
      const { status, body } = await apiJson<{ user_id: string; role: string; profile: UserProfile | null }>('/api/me');
      if (status === 401) return { kind: 'profile_missing' };
      if (status !== 200 || !body) return { kind: 'error', message: `HTTP ${status}` };
      if (!body.profile) return { kind: 'profile_missing' };
      if (!isRole(body.profile.role)) return { kind: 'invalid_role', raw: String(body.profile.role ?? '') };
      return { kind: 'ok', profile: body.profile };
    },

    async invokeEdge<T>(name: string, body: unknown): Promise<T> {
      const b = (body ?? {}) as Record<string, unknown>;
      const post = (p: string, payload: unknown) =>
        apiJson<T>(p, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
      let r: { status: number; body: T };
      switch (name) {
        case 'admin-create-user':
          r = await post('/api/admin/users', b);
          break;
        case 'admin-set-role':
          r = await post(`/api/admin/users/${b.user_id}/role`, { role: b.new_role ?? b.role });
          break;
        case 'admin-deactivate-user':
          r = await post(`/api/admin/users/${b.user_id}/deactivate`, b);
          break;
        case 'admin-delete-user':
          r = await apiJson<T>(`/api/admin/users/${b.user_id}`, { method: 'DELETE' });
          break;
        case 'audit-log-append':
          r = await post('/api/audit', b);
          break;
        default:
          throw new Error(`Edge function tidak dikenal: ${name}`);
      }
      if (r.status >= 400) throw new Error(explainError((r.body as any)?.error ?? { status: r.status }));
      return r.body;
    },

    setConfig() {
      /* no-op: configuration is server-side now */
    },
    setMode() {
      /* no-op: always Worker-backed */
    },
  };
}
